import React from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import parse from 'html-react-parser';
import { goToTop } from '_helpers';

import {
  CollectionRequirementIcon,
  DepotIcon,
  EventIcon,
} from './Images';

const Container = styled.button`
  all: initial;
  display: inline-block; 
  -moz-appearance: none;
  -webkit-appearance: none;
  background: none;
  border: none;
  outline: none;
  font-size: inherit !important;
  font-family: Roboto, sans-serif !important;

  width: 100%;
  &::-moz-focus-inner {
    border: 0;
  }

  &:focus {
    outline: 3px solid black;
    border-radius: 5px;
  }

  border-bottom: 1px solid #dbdbdb;
  background-color: transparent;
  padding: 0 !important;
  box-sizing: border-box;
  border-radius: 0 !important;
  p {
    color: #000000 !important;
  }
  
  border-bottom: 1px solid #dbdbdb;
`;

const Title = styled.p`
  all: initial;
  font-weight: 400 !important;
  font-family: Roboto, sans-serif !important;
  line-height: 24px;
  text-align: left !important;
  &::-moz-focus-inner {
    border: 0;
  }

  padding: 12px 0;
  font-size: 15px !important;
  cursor: pointer;
  display: inline-block;
  position: relative;
  width: 100%;

  &:focus {
    outline: none;
  }
`;

/**
 * Drawer: prevent the external, remote, legacy,... links to the collection requirements
 * and general pages and material pages
 *
 * @author Alireza Zebardast
 * @param {Object} link
 * @param {String} lang
 * @param {String} flag
 *
 */
const Drawer = ({ link, lang, flag }) => {
  const history = useHistory();
  const { pathname } = useLocation();

  const hasWgw = !!(pathname.includes('/wgw'));

  const isDepoList = (
    'sphere' in link
    && link.sphere === 'remote'
  ) ? link.href.includes('drop-depot-list') : null;

  const isDropEvent = (
    'sphere' in link
    && link.sphere === 'remote'
  ) ? link.href.includes('event') : null;

  const depoEventId = (
    'sphere' in link
    && link.sphere === 'remote'
  ) ? link.href.substr(27, link.href.length) : null;

  const rules = [
    // link.sphere === 'remote'
    {
      match: () => (!!((
        'sphere' in link
        && link.sphere === 'remote'
        && isDepoList
        && hasWgw
      ))), // returns boolean
      action: () => {
        history.push('/wgw/depo-list', { depoEventId, type: 'depo-list' });
      }, // returns value
    },
    {
      match: () => (!!((
        'sphere' in link
        && link.sphere === 'remote'
        && isDepoList
        && !hasWgw
      ))),
      action: () => {
        history.push('/cal/depo-list', { depoEventId, type: 'depo-list' });
      },
    },
    {
      match: () => (!!((
        'sphere' in link
        && link.sphere === 'remote'
        && isDropEvent
        && hasWgw
      ))),
      action: () => {
        history.push('/wgw/event-list', { depoEventId, type: 'event-list' });
      },
    },
    {
      match: () => (!!((
        'sphere' in link
        && link.sphere === 'remote'
        && isDropEvent
        && !hasWgw
      ))),
      action: () => {
        history.push('/cal/event-list', { depoEventId, type: 'event-list' });
      },
    },
    // ------------------------------------------------------------
    // link.type === 'remote'
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart === 'search.page'
      ))), // returns boolean
      action: () => {
        history.push('/wgw');
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && !link.hrefPart.includes('gpg')
        && link.hrefPart.includes('collection')
        && hasWgw
      ))),
      action: () => {
        const id = link.hrefPart.substring(0, link.hrefPart.length - 5);
        history.push('/wgw/collection-requirements', { id });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && !link.hrefPart.includes('gpg')
        && link.hrefPart.includes('collection')
        && !hasWgw
      ))),
      action: () => {
        const id = link.hrefPart.substring(0, link.hrefPart.length - 5);
        history.push('/cal/collection-requirements', { id });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart.includes('drop-depot')
        && hasWgw
      ))),
      action: () => {
        const id = link.hrefPart.substring(11, link.hrefPart.length - 5);
        history.push('/wgw/depo-details', { id, depoEventKind: 'depo-details' });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart.includes('drop-depot')
        && !hasWgw
      ))),
      action: () => {
        const id = link.hrefPart.substring(11, link.hrefPart.length - 5);
        history.push('/cal/depo-details', { id, depoEventKind: 'depo-details' });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart.includes('drop-event')
        && hasWgw
      ))),
      action: () => {
        const id = link.hrefPart.substring(11, link.hrefPart.length - 5);
        history.push('/wgw/depo-details', { id, depoEventKind: 'event-details' });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart.includes('drop-event')
        && !hasWgw
      ))),
      action: () => {
        const id = link.hrefPart.substring(11, link.hrefPart.length - 5);
        history.push('/cal/depo-details', { id, depoEventKind: 'event-details' });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart.includes('material')
      ))),
      action: () => {
        const matId = link.hrefPart.replace('material/', '').replace('.page', '');
        history.push('/wgw/material-details', { matId });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart.includes('gpg')
        && hasWgw
      ))),
      action: () => {
        history.push('/wgw/general-page', { link: link.hrefPart, title: link.drawer_title });
      },
    },
    {
      match: () => (!!((
        'type' in link
        && link.type === 'remote'
        && link.hrefPart.includes('gpg')
        && !hasWgw
      ))),
      action: () => {
        history.push('/cal/general-page', { link: link.hrefPart, title: link.drawer_title });
      },
    },
    // ------------------------------------------------------------
    // link.type === 'external'
    {
      match: () => (!!((
        'type' in link
        && link.type === 'external'
      ))),
      action: () => {
        window.open(link.href);
      },
    },
    // ------------------------------------------------------------
    // link.type === 'legacy'
    {
      match: () => (!!((
        'type' in link
        && link.type === 'legacy'
      ))),
      action: () => {
        history.push('/help');
      },
    },
  ];

  const identifyTarget = () => {
    goToTop();

    for (let i = 0; i < rules.length; i += 1) {
      if (rules[i].match()) {
        rules[i].action();
        return;
      }
    }
  };

  const { drawer_title } = link;

  const drawerBtnStyle = {
    border: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? 'none' : null,
    borderBottom: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? 'none' : null,
    padding: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? 0 : null,
    maxWidth: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? '80px' : null,
    margin: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? '0 10px' : null,
  };

  const titleStyle = {
    fontSize: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? '14px' : null,
    fontWeight: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? '400' : null,
    lineHeight: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? '18px' : null,
    textAlign: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? 'center' : null,
    padding: (flag === 'depot' || flag === 'event' || flag === 'collReq') ? '15px 0 5px' : null,
  };

  return (
    <Container
      onClick={identifyTarget}
      style={drawerBtnStyle}
    >
      {flag === 'collReq' && <CollectionRequirementIcon />}
      {flag === 'event' && <EventIcon />}
      {flag === 'depot' && <DepotIcon />}

      <Title
        dir={(
          (
            lang === 'fa_IR'
            || lang === 'ar_SA'
            || lang === 'ur_UR'
          )
          && !drawer_title.includes('Drop')
        ) ? 'rtl' : 'ltr'}
        style={titleStyle}
      >
        {parse(drawer_title)}
      </Title>
    </Container>
  );
};

export default Drawer;
